import * as yup from "yup";
import {capitalize} from "@mui/material";
import {FormikConfig, FormikHelpers} from "formik";
import {useUpdateMandant} from "../../client/hooks";
import {useCallback, useMemo} from "react";
import {Mandant, MandantDaten} from "../../api";
import {useMandantenHandling} from "./useMandantenHandling";
import {useSnackbar} from "notistack";
import {useMutation, useQueryClient} from "react-query";
import {useOnboardingHandling} from "../onboarding/useOnboardingHandling";

import type { MandantFormValues } from "../../types/mandantenTypes";

export function useMandantUpdateForm(mandant: Mandant): FormikConfig<MandantFormValues> {
    const {translation} = useMandantenHandling();
    const {getCountryCode, removeNumberPrefix} = useOnboardingHandling();
    const {enqueueSnackbar} = useSnackbar();
    const {mutateAsync} = useMandantUpdate(mandant.id);

    const schema = yup.object().shape({
        id: yup.string().required(capitalize(translation('client-attributes-id-required-message'))),
        name: yup.string().required(capitalize(translation('client-attributes-name-required-message'))),
        beschreibung: yup.string().nullable(),
        prompt: yup.string().nullable(),
        telefonnummer: yup.string()
            .transform((v) => (v === '' ? null : v))
            .nullable()
            .matches(/^[1-9][0-9]*$/, capitalize(translation('client-test-attributes-phone-validation-failed-message'))),
        speechModel: yup.string().transform((v) => (v === '' ? null : v)).nullable(),
        externalApiUrl: yup.string().nullable().notRequired(),
    });

    const initialTouched = useMemo(() => ({
        id: true,
        name: true,
        beschreibung: true,
        telefonnummer: true,
        prompt: true,
        externalApiUrl: true,
        speechModel: true
    }), []);

    const initialValues = useMemo(() => ({
        id: mandant.id ?? '',
        name: mandant.name ?? '',
        beschreibung: mandant.beschreibung ?? '',
        telefonnummer: removeNumberPrefix(mandant?.telefonnummer ?? ''),
        prompt: mandant.prompt ?? '',
        externalApiUrl: mandant.external_api_url ?? '',
        speechModel: mandant.speechModel ?? undefined
    }), [mandant]);

    const onSubmitUpdateForm = useCallback(async (
        daten: MandantFormValues,
        {setSubmitting, setStatus}: FormikHelpers<MandantFormValues>
    ): Promise<void> => {
        try {
            await mutateAsync({
                id: daten.id,
                name: daten.name,
                beschreibung: daten.beschreibung,
                prompt: daten.prompt,
                telefonnummer: daten.telefonnummer?.trim() ? `${getCountryCode()}${daten.telefonnummer.trim()}` : null,
                speechModel: daten.speechModel || undefined,
                external_api_url: daten.externalApiUrl || undefined,
            });
            setStatus(undefined);
            enqueueSnackbar(capitalize(translation('client-update-successful')), {
                variant: "success",
            });
        } catch (error: unknown) {
            enqueueSnackbar(capitalize(translation('client-update-failed')), {
                variant: "error",
            });
            if (error instanceof Error) {
                setStatus(error.message);
            } else {
                setStatus("An unknown error occurred.");
            }
        } finally {
            setSubmitting(false);
        }
    }, [mutateAsync, enqueueSnackbar, translation]);

    return useMemo(
        () => ({
            validationSchema: schema,
            validateOnMount: true,
            enableReinitialize: true,
            initialTouched: initialTouched,
            initialValues: initialValues,
            onSubmit: onSubmitUpdateForm
        }),
        [schema, initialValues, onSubmitUpdateForm, initialTouched]
    );
}

function useMandantUpdate(mandantId: string) {
    const updateMandant = useUpdateMandant();
    const queryClient = useQueryClient();

    return useMutation(
        (input: MandantDaten): Promise<Mandant> => {
            return updateMandant(mandantId, input);
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(["mandanten", mandantId]);
            },
        }
    );
}